import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { isAbortError } from "../../utils/api";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import type { TrackingLink } from "../../store/admin/trackingLinksStore";

export interface TrackingLinkListParams {
  page?: number;
  limit?: number;
  accessType?: string;
  status?: string;
  search?: string;
}

interface ListMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

interface TrackingLinkListBody {
  data?: TrackingLink[];
  meta?: ListMeta;
}

export interface TrackingLinkListResult {
  data: TrackingLink[];
  meta?: ListMeta;
}

interface TrackingLinkDetailBody {
  data?: ({ data?: TrackingLink } & TrackingLink) | null;
}

const fetchTrackingLink = (
  id: string | null,
  signal?: AbortSignal,
): Promise<TrackingLink | null> =>
  getJson<TrackingLinkDetailBody>("/trackingLinks/link", { id }, { signal })
    .then((body) => body.data?.data ?? body.data ?? null)
    .catch((error) => {
      if (isAbortError(error)) throw error;
      return null;
    });

export const trackingLinkQueryOptions = (
  cid: string | null,
  id: string | null,
) => ({
  queryKey: qk.trackingLinks.detail(cid, id),
  queryFn: ({ signal }: { signal?: AbortSignal }) => fetchTrackingLink(id, signal),
  retry: false,
});

export function useTrackingLink(id: string | null | undefined, enabled = true) {
  const cid = useCompaniesStore((s) => s.companyId);
  return useQuery({
    ...trackingLinkQueryOptions(cid, id ?? null),
    enabled: enabled && !!id,
  });
}

export function useTrackingLinksList(params: TrackingLinkListParams = {}) {
  const cid = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.trackingLinks.list(cid, params),
    queryFn: ({ signal }) =>
      getJson<TrackingLinkListBody>(
        "/trackingLinks",
        { page: 1, limit: 6, ...params },
        { signal },
      ).then((body) => ({ data: body.data ?? [], meta: body.meta })),
  });
}
